import Phaser from "phaser";
import { audioManager } from "../audio/AudioManager";
import {
  GAME_HEIGHT,
  GAME_WIDTH,
  PLAYER,
  SCREEN_WRAP_PADDING,
  TORPEDO,
  WAVES,
} from "../config";
import { gameStore } from "../state/GameStore";
import { type BorgCubeSprite, BorgManager, preloadBorgSprites } from "./game/BorgCube";
import { Combat } from "./game/Combat";
import { Player, preloadPlayerSprites } from "./game/Player";
import { Starfield } from "./game/Starfield";
import { GameUI } from "./game/UI";
import { type TorpedoSprite, WeaponSystem } from "./game/weapons/WeaponSystem";

interface WASDKeys {
  up: Phaser.Input.Keyboard.Key;
  down: Phaser.Input.Keyboard.Key;
  left: Phaser.Input.Keyboard.Key;
  right: Phaser.Input.Keyboard.Key;
}

export class GameScene extends Phaser.Scene {
  private starfield!: Starfield;
  private player!: Player;
  private borgManager!: BorgManager;
  private weaponSystem!: WeaponSystem;
  private ui!: GameUI;
  private combat!: Combat;

  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private spaceKey!: Phaser.Input.Keyboard.Key;
  private torpedoKey!: Phaser.Input.Keyboard.Key;
  private wasd!: WASDKeys;

  private gameOver = false;

  constructor() {
    super({ key: "GameScene" });
  }

  preload(): void {
    // Load sprites - each module owns its sprite loading
    preloadPlayerSprites(this.load);
    preloadBorgSprites(this.load);
  }

  create(): void {
    // Initialize audio on first user interaction
    this.input.once("pointerdown", () => audioManager.init());
    this.input.keyboard?.once("keydown", () => audioManager.init());

    // Reset game state
    gameStore.reset();
    this.gameOver = false;

    // Create game components
    this.starfield = new Starfield(this);
    this.starfield.create();

    this.player = new Player(this);
    this.player.create();

    this.borgManager = new BorgManager(this);
    this.borgManager.create();

    // Create weapon system (needs player ship and borg group)
    this.weaponSystem = new WeaponSystem(
      this,
      this.player.ship,
      this.borgManager.cubes,
    );
    this.weaponSystem.create();

    // Wire up phaser damage callback
    this.weaponSystem.phasers.onDamage = (target, damage) => {
      this.phaserHitBorg(target, damage);
    };

    this.ui = new GameUI(this);
    this.ui.create();

    this.combat = new Combat(this);

    // Spawn initial Borg cubes
    for (let i = 0; i < gameStore.borgCount; i++) {
      this.borgManager.spawn("large", this.player.ship.x, this.player.ship.y);
    }

    // Input - keyboard is required for this game
    const keyboard = this.input.keyboard;
    if (!keyboard) {
      throw new Error("Keyboard input is required");
    }
    this.cursors = keyboard.createCursorKeys();
    this.spaceKey = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this.torpedoKey = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.E);
    this.wasd = {
      up: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      down: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      left: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      right: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D),
    };

    // Collisions
    this.physics.add.overlap(
      this.weaponSystem.torpedoes.torpedoes,
      this.borgManager.cubes,
      this.torpedoHitBorg as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    );
    this.physics.add.overlap(
      this.player.ship,
      this.borgManager.cubes,
      this.shipHitBorg as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this,
    );
  }

  update(time: number, delta: number): void {
    if (this.gameOver) return;

    // Update starfield parallax
    this.starfield.update(this.player.getVelocity());

    // Ship controls
    const controls = {
      rotateLeft: this.cursors.left.isDown || this.wasd.left.isDown,
      rotateRight: this.cursors.right.isDown || this.wasd.right.isDown,
      thrust: this.cursors.up.isDown || this.wasd.up.isDown,
    };

    this.player.update(time, delta, controls);

    // Weapons
    // Phasers (SPACE) - acquire target on key-down, hold to keep firing
    if (Phaser.Input.Keyboard.JustDown(this.spaceKey)) {
      this.weaponSystem.phasers.tryAcquireTarget();
    } else if (!this.spaceKey.isDown) {
      this.weaponSystem.phasers.stop();
    }

    // Torpedoes (E) - tap to fire projectile
    if (Phaser.Input.Keyboard.JustDown(this.torpedoKey)) {
      this.weaponSystem.torpedoes.fire(time);
    }

    // Update weapon systems
    this.weaponSystem.update(delta, this.screenWrap.bind(this));

    // Screen wrap for ship
    this.screenWrap(this.player.ship);

    // Screen wrap for Borg cubes
    this.borgManager.updateScreenWrap(this.screenWrap.bind(this));

    // Check for wave completion
    if (this.borgManager.getActiveCount() === 0) {
      this.nextWave();
    }

    // Flash ship if invulnerable
    if (gameStore.invulnerable) {
      this.player.setInvulnerable(true, Math.sin(time * 0.02) * 0.5 + 0.5);
    }
  }

  private torpedoHitBorg(
    torpedoObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    cubeObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ): void {
    const torpedo = torpedoObj as TorpedoSprite;
    const cube = cubeObj as BorgCubeSprite;

    torpedo.destroy();
    cube.health -= TORPEDO.DAMAGE;

    // Flash effect
    this.combat.flashSprite(cube);

    // Update damage visual
    this.borgManager.updateDamageVisual(cube);

    if (cube.health <= 0) {
      this.destroyBorg(cube);
    }
  }

  private phaserHitBorg(cube: BorgCubeSprite, damage: number): void {
    cube.health -= damage;

    // Flash effect (less intense for sustained damage)
    this.combat.flashSprite(cube, 0.3);

    // Update damage visual
    this.borgManager.updateDamageVisual(cube);

    if (cube.health <= 0) {
      this.destroyBorg(cube);
    }
  }

  private destroyBorg(cube: BorgCubeSprite): void {
    // Notify weapons that target is destroyed (stops phaser beam)
    this.weaponSystem.notifyTargetDestroyed(cube);

    this.combat.playExplosionSound();

    // Score based on size
    const points = this.borgManager.getPoints(cube.borgSize);
    gameStore.addScore(points);
    this.ui.updateScore(gameStore.score);

    // Create explosion particles
    this.combat.createExplosion(cube.x, cube.y);

    // Split into smaller cubes
    if (cube.borgSize === "large") {
      this.borgManager.spawnAtPosition("medium", cube.x - 20, cube.y);
      this.borgManager.spawnAtPosition("medium", cube.x + 20, cube.y);
    } else if (cube.borgSize === "medium") {
      this.borgManager.spawnAtPosition("small", cube.x - 15, cube.y);
      this.borgManager.spawnAtPosition("small", cube.x + 15, cube.y);
    }

    cube.destroy();
  }

  private shipHitBorg(
    shipObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
    cubeObj: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  ): void {
    if (gameStore.invulnerable || this.gameOver) return;

    const ship = shipObj as Phaser.Physics.Arcade.Sprite;
    const cube = cubeObj as BorgCubeSprite;

    this.combat.playHitSound();

    // Damage based on cube size
    const damage = this.borgManager.getDamage(cube.borgSize);
    gameStore.takeDamage(damage);
    this.ui.updateHealth(gameStore.health);

    // Invulnerability frames
    gameStore.setInvulnerable(true);
    this.time.delayedCall(PLAYER.INVULNERABILITY_DURATION, () => {
      gameStore.setInvulnerable(false);
      this.player.setInvulnerable(false);
    });

    // Knockback
    this.combat.applyKnockback(ship, cube.x, cube.y);

    // Screen shake
    this.combat.shakeCamera();

    // Check for death
    if (!gameStore.isAlive) {
      this.playerDeath();
    }
  }

  private playerDeath(): void {
    this.gameOver = true;
    this.combat.playGameOverSound();

    // Stop all weapons
    this.weaponSystem.hide();

    // Explosion at ship location
    this.combat.createShipExplosion(this.player.ship.x, this.player.ship.y);

    this.player.hide();

    // Game Over Screen
    this.time.delayedCall(1000, () => {
      this.ui.showGameOver(gameStore.score, gameStore.waveNumber);
    });
  }

  private nextWave(): void {
    gameStore.nextWave();
    this.ui.updateWave(gameStore.waveNumber);

    // Wave announcement
    this.ui.showWaveAnnouncement(gameStore.waveNumber);

    // Small health bonus
    gameStore.heal(WAVES.HEALTH_BONUS);
    this.ui.updateHealth(gameStore.health);

    // Spawn new wave
    this.time.delayedCall(WAVES.SPAWN_DELAY, () => {
      for (let i = 0; i < gameStore.borgCount; i++) {
        this.borgManager.spawn("large", this.player.ship.x, this.player.ship.y);
      }
    });
  }

  private screenWrap(sprite: Phaser.Physics.Arcade.Sprite): void {
    if (sprite.x < -SCREEN_WRAP_PADDING)
      sprite.x = GAME_WIDTH + SCREEN_WRAP_PADDING;
    if (sprite.x > GAME_WIDTH + SCREEN_WRAP_PADDING)
      sprite.x = -SCREEN_WRAP_PADDING;
    if (sprite.y < -SCREEN_WRAP_PADDING)
      sprite.y = GAME_HEIGHT + SCREEN_WRAP_PADDING;
    if (sprite.y > GAME_HEIGHT + SCREEN_WRAP_PADDING)
      sprite.y = -SCREEN_WRAP_PADDING;
  }
}
